import { axios } from "utils";

import { PUBLIC_FOOTPRINTING_URL as endpoint } from "consts";

import {
  CalculatedCropFootprintResponse,
  CalculatedFarmFootprintResponse,
  CropAssessment,
  CropFootprintReturnBody,
  CropSaleData,
  DairyFeedProduction,
  DairyFootprintReturnBody,
  EmissionsFactorRefrigerant,
  FarmFootprintReturnBody,
  FarmLandUseChange,
  FootprintingCropFertilizer,
  FootprintingCropPrimaryData,
  FootprintingCropSalesPayload,
  FootprintingCropSeeding,
  FootprintingCropTillage,
  FootprintingDairyCowSales,
  FootprintingDairyFeedAdditivePayload,
  FootprintingDairyFeedInformation,
  FootprintingDairyHerdDetails,
  FootprintingDairyHerdSize,
  FootprintingDairyManureAdditivePayload,
  FootprintingDairyManureStoragePayload,
  FootprintingDairyPrimaryData,
  FootprintingFarmCropPlantProtectionPayload,
  FootprintingFarmPrimaryData,
  FootprintingFarmRefrigerantPayload,
  FootprintingFarmRefrigerantUsage,
  FootprintingFarmResourceConsumption,
  FootprintingFarmRetailerPayload,
  LandUseChangeRequestPayload,
  PurchaserData,
  RetailerPurchase,
  UploadableCowSales,
  UploadableFeedAdditive,
  UploadableFeedInformation,
  UploadableHerdSize,
  UploadableManureAdditive,
  UploadableManureStorage,
  UploadablePlantProtection,
} from "models";

// Emission Factors

export const getFootprintingEmissionFactorsRefrigerants = async (): Promise<EmissionsFactorRefrigerant[]> =>
  axios.get(`${endpoint}/emission-factors/refrigerants`);

// Farm Data

export const getCalculatedFarmFootprintByYear = async (year: number): Promise<CalculatedFarmFootprintResponse> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/footprint-calculation`, { data: { year }, hideErrorMessage: true });

export const getFarmFootprintByYear = async (year: number): Promise<FarmFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints`, { data: { year }, hideErrorMessage: true });

export const getFootprintingFarmPrimaryDataById = async (primaryDataId: string): Promise<FootprintingFarmPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const getFootprintingFarmPrimaryDataByYear = async (year: number): Promise<FootprintingFarmPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/primary-data`, { data: { year }, hideErrorMessage: true });

export const getFootprintingFarmResourceConsumptionByPrimaryDataId = async (
  footprintingFarmPrimaryDataId: string
): Promise<FootprintingFarmResourceConsumption> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/resource-consumption`, { data: { footprintingFarmPrimaryDataId }, hideErrorMessage: true });

export const setFootprintingFarmResourceConsumption = async (
  data: FootprintingFarmResourceConsumption,
  primaryDataId?: string
): Promise<FootprintingFarmResourceConsumption> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/farm-footprints/${primaryDataId}/resource-consumption`, data);
  }

  return axios.post(`${endpoint}/farm-footprints/resource-consumption`, data);
};

export const getFootprintingFarmRefrigerantUsageByPrimaryDataId = async (footprintingFarmPrimaryDataId: string): Promise<FootprintingFarmRefrigerantUsage[]> =>
  axios.get(`${endpoint}/farm-footprints/refrigerant-usage`, { data: { footprintingFarmPrimaryDataId } });

export const setFootprintingFarmRefrigerantUsage = async (
  data: FootprintingFarmRefrigerantPayload,
  primaryDataId?: string
): Promise<FootprintingFarmRefrigerantUsage[]> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/farm-footprints/${primaryDataId}/refrigerant-usage`, data);
  }

  return axios.post(`${endpoint}/farm-footprints/refrigerant-usage`, data);
};

export const getFootprintingFarmRetailers = async (farmId: string): Promise<RetailerPurchase[]> =>
  axios.get(`${endpoint}/farm-footprints/retailers?farmId=${farmId}`);

export const setFootprintingFarmResourceRetailers = async (data: FootprintingFarmRetailerPayload, primaryDataId?: string): Promise<RetailerPurchase[]> =>
  axios.put(`${endpoint}/farm-footprints/${primaryDataId}/purchases`, data);

export const getFootprintingFarmPurchases = async (primaryDataId: string): Promise<RetailerPurchase[]> =>
  axios.get(`${endpoint}/farm-footprints/${primaryDataId}/purchases`);

export const setFootprintingFarmPrimaryData = async (
  data: Omit<FootprintingFarmPrimaryData, "id">,
  primaryDataId?: string
): Promise<FootprintingFarmPrimaryData> => {
  if (primaryDataId && primaryDataId !== "") {
    return axios.put(`${endpoint}/farm-footprints/${primaryDataId}/primary-data`, data);
  }

  return axios.post(`${endpoint}/farm-footprints/primary-data`, data);
};

export const getFarmLandUseChanges = async (farmId: string, year?: number, range?: number): Promise<FarmLandUseChange[]> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/${farmId}/land-use-change?year=${year ?? ""}&range=${range ?? ""}`, { hideErrorMessage: true });

export const setFarmLandUseChanges = async (farmId: string, data: LandUseChangeRequestPayload, year: number, range: number): Promise<FarmLandUseChange[]> =>
  axios.put(`${endpoint}/farm-footprints/${farmId}/land-use-change?year=${year ?? ""}&range=${range ?? ""}`, data);

// Crop Data

export const getCalculatedCropFootprintByCropIdAndYear = async (cropId: string, year: number): Promise<CalculatedCropFootprintResponse> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/crop-footprints/footprint-calculation`, { data: { cropId, year }, hideErrorMessage: true });

export const getCropFootprintByCropIdAndYear = async (cropId: string, year: number): Promise<CropFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/crop-footprints`, { data: { cropId, year }, hideErrorMessage: true });

export const getFootprintingCropPrimaryDataById = async (primaryDataId: string): Promise<FootprintingCropPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const getFootprintingCropPrimaryDataByCropIdAndYear = async (cropId: string, year: number): Promise<FootprintingCropPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/crop-footprints/primary-data`, { data: { cropId, year }, hideErrorMessage: true });

export const setFootprintingCropPrimaryData = async (
  data: Omit<FootprintingCropPrimaryData, "id">,
  primaryDataId?: string
): Promise<FootprintingCropPrimaryData> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/crop-footprints/${primaryDataId}/primary-data`, data);
  }

  return axios.post(`${endpoint}/crop-footprints/primary-data`, data);
};

export const deleteFootprintingCropPrimaryData = async (primaryDataId: string) => axios.delete(`${endpoint}/crop-footprints/${primaryDataId}/primary-data`);

export const getFootprintingCropPlantProtection = async (primaryDataId: string): Promise<UploadablePlantProtection[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/plant-protection`);

export const setFootprintingCropPlantProtection = async (
  data: FootprintingFarmCropPlantProtectionPayload,
  primaryDataId?: string
): Promise<UploadablePlantProtection[]> => axios.put(`${endpoint}/crop-footprints/${primaryDataId}/plant-protection`, data.selectedPlantProtection);

export const getFootprintingCropSeedingsByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropSeeding[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/seedings`);

export const setFootprintingCropSeedingsByPrimaryDataId = async (data: FootprintingCropSeeding[], primaryDataId: string) =>
  axios.put(`${endpoint}/crop-footprints/${primaryDataId}/seedings`, data);

export const getFootprintingCropTillagesByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropTillage[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/tillages`);

export const setFootprintingCropTillagesByPrimaryDataId = async (data: FootprintingCropTillage[], primaryDataId: string) =>
  axios.put(`${endpoint}/crop-footprints/${primaryDataId}/tillages`, data);

export const getFootprintingCropFertilizersMineralsByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropFertilizer[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/fertilizers-minerals`, { data: { primaryDataId } });

export const setFootprintingCropFertilizersMinerals = async (
  data: FootprintingCropFertilizer[],
  primaryDataId: string
): Promise<FootprintingCropFertilizer[]> => axios.put(`${endpoint}/crop-footprints/${primaryDataId}/fertilizers-minerals`, data);

export const getFootprintingCropFertilizersOrganicsByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropFertilizer[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/fertilizers-organics`, { data: { primaryDataId } });

export const setFootprintingCropFertilizersOrganics = async (
  data: FootprintingCropFertilizer[],
  primaryDataId?: string
): Promise<FootprintingCropFertilizer[]> => axios.put(`${endpoint}/crop-footprints/${primaryDataId}/fertilizers-organics`, data);

export const getFootprintingCropPurchasersByFarmId = async (farmId: string): Promise<PurchaserData[]> =>
  axios.get(`${endpoint}/crop-footprints/purchasers?farmId=${farmId}`);

export const getFootprintingCropSalesByPrimaryDataId = async (primaryDataId: string): Promise<CropSaleData[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/sales`);

export const setFootprintingCropSales = async (data: FootprintingCropSalesPayload, primaryDataId: string): Promise<CropSaleData[]> =>
  axios.put(`${endpoint}/crop-footprints/${primaryDataId}/sales`, data);

export const getFootprintingCropAssessmentsRequiringReview = async (farmId: string): Promise<{ [year: string]: { [cropId: string]: CropAssessment } }> =>
  axios.get(`${endpoint}/crop-assessments-requiring-review?farmId=${farmId}`);

// Dairy Data

export const getDairyFootprintByYear = async (year: number): Promise<DairyFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/dairy-footprints`, { data: { year }, hideErrorMessage: true });

export const getFootprintingDairyPrimaryDataById = async (primaryDataId: string): Promise<FootprintingDairyPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const getFootprintingDairyPrimaryDataByYear = async (year: number): Promise<FootprintingDairyPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/dairy-footprints/primary-data`, { data: { year }, hideErrorMessage: true });

export const setFootprintingDairyPrimaryData = async (
  data: Omit<FootprintingDairyPrimaryData, "id">,
  primaryDataId?: string
): Promise<FootprintingDairyPrimaryData> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/primary-data`, data);
  }

  return axios.post(`${endpoint}/dairy-footprints/primary-data`, data);
};

export const getDairyHerdSize = async (primaryDataId: string): Promise<FootprintingDairyHerdSize[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/herd-size`);

export const setDairyHerdSize = async (primaryDataId: string, data: UploadableHerdSize[]): Promise<FootprintingDairyHerdSize[]> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/herd-size`, data);

export const getDairyHerdDetails = async (primaryDataId: string): Promise<FootprintingDairyHerdDetails> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/herd-details`);

export const setDairyHerdDetails = async (data: Omit<FootprintingDairyHerdDetails, "id">, primaryDataId?: string): Promise<FootprintingDairyHerdDetails> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/herd-details`, data);
  }

  return axios.post(`${endpoint}/dairy-footprints/herd-details`, data);
};

export const getDairyCowSales = async (primaryDataId: string): Promise<FootprintingDairyCowSales> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/cow-sales`);

export const setDairyCowSales = async (primaryDataId: string, data: UploadableCowSales): Promise<FootprintingDairyCowSales> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/cow-sales`, data);

export const getFootprintingDairyFeedAdditive = async (primaryDataId: string): Promise<UploadableFeedAdditive[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/feed-additive`);

export const setFootprintingDairyFeedAdditive = async (data: FootprintingDairyFeedAdditivePayload, primaryDataId?: string): Promise<UploadableFeedAdditive[]> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/feed-additive`, data.selectedFeedAdditive);

export const getDairyFeedInformation = async (primaryDataId: string): Promise<FootprintingDairyFeedInformation[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/feed-information`);

export const setDairyFeedInformation = async (primaryDataId: string, data: UploadableFeedInformation[]): Promise<FootprintingDairyFeedInformation> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/feed-information`, data);

export const getFootprintingDairyFeedProduction = async (feedInformationId: string): Promise<DairyFeedProduction | null> =>
  axios.get(`${endpoint}/dairy-footprints/dairy-feed-production/${feedInformationId}`);

export const setFootprintingDairyFeedProduction = async (feedInformationId: string, data: DairyFeedProduction): Promise<DairyFeedProduction> =>
  axios.put(`${endpoint}/dairy-footprints/dairy-feed-production/${feedInformationId}`, data);

export const getFootprintingDairyManureStorage = async (primaryDataId: string): Promise<UploadableManureStorage[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/manure-storage`);

export const setFootprintingDairyManureStorage = async (
  data: FootprintingDairyManureStoragePayload,
  primaryDataId?: string
): Promise<UploadableManureStorage[]> => axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/manure-storage`, data.selectedManureStorage);

export const getFootprintingDairyManureAdditive = async (primaryDataId: string): Promise<UploadableManureAdditive[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/manure-additive`);

export const setFootprintingDairyManureAdditive = async (
  data: FootprintingDairyManureAdditivePayload,
  primaryDataId?: string
): Promise<UploadableManureAdditive[]> => axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/manure-additive`, data.selectedManureAdditive);
